import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Users,
  FileText,
  DollarSign,
  Target,
  Code,
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { Project } from '../../types/collaborators';
import { ExtendedProject, BudgetMilestone } from '../../types/budgetSync';
import { contactsService, companiesService, collaboratorsService, supabase } from '../../services/supabaseService';

interface ProjectModalExtendedProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Partial<Project>) => void;
  project?: ExtendedProject | null;
}

const ProjectModalExtended: React.FC<ProjectModalExtendedProps> = ({
  isOpen,
  onClose,
  onSave,
  project
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'budget' | 'milestones' | 'tech' | 'team'>('details');
  const [formData, setFormData] = useState({
    name: '',
    clientId: '',
    clientName: '',
    description: '',
    status: 'active' as Project['status'],
    assignedUsers: [] as string[],
    budget: 0,
    total_hours: 0,
    hourly_rate: 0,
    start_date: '',
    end_date: '',
    duration_months: 0,
    tech_stack: [] as string[],
    milestones: [] as BudgetMilestone[]
  });

  const [availableClients, setAvailableClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isSynced = !!project?.external_id;

  useEffect(() => {
    if (isOpen) {
      loadClients();
      loadUsersAndCollaborators();
    }
  }, [isOpen]);

  useEffect(() => {
    if (project) {
      // El service devuelve camelCase; algunos flujos legacy (sync desde
      // presupuestos) todavía envían snake. Aceptamos ambos.
      const p = project as any;
      const startDateRaw = p.startDate || p.start_date;
      const endDateRaw = p.endDate || p.end_date;
      setFormData({
        name: p.name,
        clientId: p.clientId || p.client_id || '',
        clientName: p.clientName || p.client_name || '',
        description: p.description || '',
        status: p.status,
        assignedUsers: p.assignedUsers || p.assigned_users || [],
        budget: p.budget || 0,
        total_hours: p.totalHours ?? p.total_hours ?? 0,
        hourly_rate: p.hourlyRate ?? p.hourly_rate ?? 0,
        start_date: startDateRaw ? new Date(startDateRaw).toISOString().split('T')[0] : '',
        end_date: endDateRaw ? new Date(endDateRaw).toISOString().split('T')[0] : '',
        duration_months: p.durationMonths ?? p.duration_months ?? 0,
        tech_stack: p.techStack || p.tech_stack || [],
        milestones: p.milestones || []
      });
    } else {
      setFormData({
        name: '',
        clientId: '',
        clientName: '',
        description: '',
        status: 'active',
        assignedUsers: [],
        budget: 0,
        total_hours: 0,
        hourly_rate: 0,
        start_date: '',
        end_date: '',
        duration_months: 0,
        tech_stack: [],
        milestones: []
      });
    }
  }, [project]);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const [persons, companies] = await Promise.all([
        contactsService.getPersons(),  // ← solo personas (no las empresas duplicadas)
        companiesService.getAll()      // ← empresas ya unificadas en contacts
      ]);

      const clients = [
        ...companies.map((c: any) => ({ id: c.id, name: c.name, type: 'company' })),
        ...persons.map((c: any) => ({
          id: c.id,
          name: c.company ? `${c.fullName} (${c.company})` : c.fullName,
          type: 'contact',
        })),
      ];

      setAvailableClients(clients);
    } catch (error) {
      console.error('Error loading clients:', error);
      setAvailableClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadUsersAndCollaborators = async () => {
    try {
      setLoadingUsers(true);
      const collaborators = await collaboratorsService.getAll();

      const collaboratorsList = collaborators
        .filter(c => c.isActive)
        .map(c => ({
          id: c.id,
          name: c.name,
          email: c.email,
          type: 'collaborator',
          has_crm_access: c.has_crm_access,
          user_id: c.user_id
        }));

      setAvailableUsers(collaboratorsList);
    } catch (error) {
      console.error('Error loading collaborators:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    const isAssigned = formData.assignedUsers.includes(userId);
    setFormData({
      ...formData,
      assignedUsers: isAssigned
        ? formData.assignedUsers.filter(id => id !== userId)
        : [...formData.assignedUsers, userId]
    });
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = availableClients.find(c => c.id === clientId);
    setFormData({
      ...formData,
      clientId,
      clientName: selectedClient?.name || ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim() === '') {
      alert('Por favor ingresa un nombre para el proyecto');
      return;
    }

    if (!formData.clientId) {
      alert('Por favor selecciona un cliente');
      return;
    }

    if (formData.budget && formData.budget < 0) {
      alert('El presupuesto no puede ser negativo');
      return;
    }

    if (formData.hourly_rate && formData.hourly_rate < 0) {
      alert('La tarifa horaria no puede ser negativa');
      return;
    }

    if (formData.total_hours && formData.total_hours < 0) {
      alert('Las horas totales no pueden ser negativas');
      return;
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        alert('La fecha de fin no puede ser anterior a la fecha de inicio');
        return;
      }
    }

    onSave(formData);
  };

  const getTotalMilestoneHours = () => {
    return formData.milestones.reduce((total, milestone) => {
      const milestoneHours = milestone.tasks.reduce((sum, task) => sum + task.hours, 0);
      return total + milestoneHours;
    }, 0);
  };

  // Milestones y Tech Stack retirados 2026-09-19: sustituidos por el
  // sistema de Tareas + Documentos del ProjectDetailPage. El código de
  // esos tabs se conserva en el archivo por si se reactivan.
  const tabs = [
    { id: 'details', label: 'Detalles', icon: Building2 },
    { id: 'budget', label: 'Presupuesto', icon: DollarSign },
    { id: 'team', label: 'Equipo', icon: Users }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {project ? 'Ver/Editar Proyecto' : 'Nuevo Proyecto'}
            </h2>
            {isSynced && (
              <div className="flex items-center space-x-2 mt-1">
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                  Sincronizado desde Presupuesto
                </span>
                {project?.budget_id && (
                  <span className="text-xs text-gray-500">ID: {project.budget_id}</span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del proyecto *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={isSynced}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  required
                  value={formData.clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  disabled={loadingClients || isSynced}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                >
                  <option value="">
                    {loadingClients ? 'Cargando clientes...' : 'Seleccionar cliente'}
                  </option>
                  {availableClients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado del proyecto
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as Project['status'] })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Activo</option>
                  <option value="paused">Pausado</option>
                  <option value="completed">Completado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={isSynced}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    disabled={isSynced}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    disabled={isSynced}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Budget Tab */}
          {activeTab === 'budget' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Presupuesto total *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">€</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.budget}
                      onChange={(e) =>
                        setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })
                      }
                      disabled={isSynced}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                      placeholder="12000"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Importe total facturable al cliente.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horas contratadas
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.total_hours}
                      onChange={(e) =>
                        setFormData({ ...formData, total_hours: parseFloat(e.target.value) || 0 })
                      }
                      disabled={isSynced}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                      placeholder="120"
                    />
                    <span className="absolute right-3 top-2.5 text-gray-500">h</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Bolsa de horas acordada con el cliente.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarifa horaria interna
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-500">€</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) =>
                        setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })
                      }
                      disabled={isSynced}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                      placeholder="45"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Coste por hora que asume Cero Ideas.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (meses)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.duration_months}
                    onChange={(e) =>
                      setFormData({ ...formData, duration_months: parseInt(e.target.value) || 0 })
                    }
                    disabled={isSynced}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                    placeholder="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coste interno estimado
                  </label>
                  <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
                    €{(formData.total_hours * formData.hourly_rate).toLocaleString('es-ES')}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    horas × tarifa horaria
                  </p>
                </div>
              </div>

              {formData.budget > 0 && formData.total_hours > 0 && formData.hourly_rate > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm">
                  <p className="text-green-900">
                    <strong>Margen estimado:</strong>{' '}
                    €{(formData.budget - formData.total_hours * formData.hourly_rate).toLocaleString('es-ES')}
                    {' '}({(((formData.budget - formData.total_hours * formData.hourly_rate) / formData.budget) * 100).toFixed(1)}%)
                  </p>
                </div>
              )}

              {project?.tasks && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Progreso del Proyecto</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tareas completadas</span>
                      <span className="font-medium">
                        {project.tasks.filter((t) => t.status === 'done').length} /{' '}
                        {project.tasks.length}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{
                          width: `${
                            (project.tasks.filter((t) => t.status === 'done').length /
                              project.tasks.length) *
                            100
                          }%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Milestones Tab */}
          {activeTab === 'milestones' && (
            <div className="space-y-4">
              {formData.milestones.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No hay milestones definidos para este proyecto</p>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-900">
                          {formData.milestones.length} Milestones
                        </p>
                        <p className="text-sm text-blue-700">
                          Total: {getTotalMilestoneHours()} horas estimadas
                        </p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    {formData.milestones.map((milestone, index) => {
                      const milestoneHours = milestone.tasks.reduce(
                        (sum, task) => sum + task.hours,
                        0
                      );
                      return (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">
                                {milestone.title}
                              </h4>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span>{milestone.tasks.length} tareas</span>
                                <span>{milestoneHours} horas</span>
                              </div>
                            </div>
                            <CheckCircle className="w-5 h-5 text-gray-400" />
                          </div>

                          <div className="space-y-2">
                            {milestone.tasks.map((task, taskIndex) => (
                              <div
                                key={taskIndex}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded"
                              >
                                <span className="text-sm text-gray-700">{task.description}</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {task.hours}h
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tech Stack Tab */}
          {activeTab === 'tech' && (
            <div className="space-y-4">
              {formData.tech_stack.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Code className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No hay tecnologías definidas para este proyecto</p>
                </div>
              ) : (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <p className="font-medium text-purple-900">
                      {formData.tech_stack.length} Tecnologías
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {formData.tech_stack.map((tech, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-lg"
                      >
                        <span className="font-medium text-gray-900">{tech}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Colaboradores Asignados</h3>

              {loadingUsers ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Cargando colaboradores...</span>
                </div>
              ) : availableUsers.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">
                    No hay colaboradores activos disponibles
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {availableUsers.map((user) => {
                    const hasCrmAccess = (user as any).has_crm_access;

                    return (
                      <label
                        key={user.id}
                        className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assignedUsers.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            {hasCrmAccess && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                CRM
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {formData.assignedUsers.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    <strong>{formData.assignedUsers.length}</strong> colaborador(es) asignado(s)
                  </p>
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          {isSynced && (
            <div className="text-sm text-gray-600">
              <FileText className="w-4 h-4 inline mr-1" />
              Proyecto sincronizado - Algunos campos no son editables
            </div>
          )}
          {!isSynced && <div></div>}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {project ? 'Actualizar' : 'Crear'} Proyecto
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModalExtended;
